import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useRef, useState } from 'react';
import type { PaletteColorName } from '@/features/colors/color.types';
import { useCategoryOptionColorsResolver } from '@/features/colors/hooks/useCategoryOptionColorsResolver';
import { toPaletteColorName } from '@/features/colors/lib/paletteLookup';
import { useDeleteCategory } from '@/features/eventMetadata/hooks/useDeleteCategory';
import { useUpdateCategory } from '@/features/eventMetadata/hooks/useUpdateCategory';
import type { Category } from '@/features/eventMetadata/types';
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@/ui';
import { ColorSwatchPopover } from './ColorSwatchPopover';

type Props = {
  category: Category;
};

export const ColorCategoryRow = ({ category }: Props) => {
  const { updateCategory } = useUpdateCategory();
  const { removeCategory } = useDeleteCategory();

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const inputRef = useRef<HTMLInputElement>(null);
  const [localText, setLocalText] = useState(category.text ?? '');
  const [isFocused, setIsFocused] = useState(false);

  // Re-sync the input when the prop changes externally (e.g. optimistic rollback)
  // but only while the user isn't actively editing.
  useEffect(() => {
    if (!isFocused) {
      setLocalText(category.text ?? '');
    }
  }, [category.text, isFocused]);

  const resolveOptionColors = useCategoryOptionColorsResolver();
  // The category's identity is its palette slot; the swatch displays the slot
  // resolved through the experience palette so the row matches how the
  // category actually renders.
  const currentColorName = toPaletteColorName(category.colorName);
  const { backgroundColor: displayColor } =
    resolveOptionColors(currentColorName);

  const handleColorChange = (colorName: PaletteColorName) => {
    updateCategory({
      id: category.id,
      colorName,
      text: localText.trim() || null,
    });
  };

  const handleTextSave = () => {
    const newText = localText.trim();
    if (newText !== (category.text ?? '').trim()) {
      updateCategory({
        id: category.id,
        // Preserve the existing palette slot so a rename never changes it.
        colorName: category.colorName,
        text: newText || null,
      });
    }
  };

  return (
    <Flex
      align="center"
      bg="white"
      border="1px solid"
      borderColor="line.DEFAULT"
      borderRadius="ah-xl"
      gap={2}
      opacity={isDragging ? 0.6 : 1}
      position="relative"
      px={3}
      py={2.5}
      ref={setNodeRef}
      zIndex={isDragging ? 1 : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {/* Drag handle — the activator lives here so dragging (pointer or
          keyboard) never interferes with editing the name, picking a color,
          or opening the menu. */}
      <IconButton
        aria-label="Reorder category"
        color="ink.muted"
        cursor="grab"
        flexShrink={0}
        icon={<Icon icon="GripVertical" size={4} />}
        minW={7}
        ref={setActivatorNodeRef}
        size="ah-sm"
        sx={{ touchAction: 'none' }}
        variant="ahGhost"
        {...attributes}
        {...listeners}
      />

      {/* Color swatch → opens color picker popover */}
      <ColorSwatchPopover
        currentColorName={currentColorName}
        trigger={
          <Box
            _hover={{ opacity: 0.85 }}
            as="button"
            bg={displayColor}
            border="2px solid"
            borderColor="rgba(0,0,0,0.06)"
            borderRadius="ah-md"
            cursor="pointer"
            flexShrink={0}
            h="26px"
            transition="opacity 0.12s"
            type="button"
            w="26px"
          />
        }
        onChange={handleColorChange}
      />

      {/* Editable name input */}
      <Input
        _focus={{ bg: 'magicPurple.25', borderColor: 'magicPurple.300' }}
        _hover={{ bg: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.12)' }}
        aria-label="Category name"
        border="1px solid transparent"
        borderRadius="ah-md"
        color="ink.strong"
        flex={1}
        fontSize="ah-control"
        fontWeight={600}
        h="auto"
        minH={0}
        px={2}
        py={1}
        ref={inputRef}
        size="sm"
        value={localText}
        onChange={(e) => setLocalText(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          handleTextSave();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.blur();
          if (e.key === 'Escape') {
            setLocalText(category.text ?? '');
            inputRef.current?.blur();
          }
        }}
      />

      {/* Three-dots context menu */}
      <Menu placement="bottom-end">
        <MenuButton
          _hover={{ bg: 'rgba(0,0,0,0.06)', color: 'ink.strong' }}
          alignItems="center"
          as={Box}
          borderRadius="ah-md"
          color="ink.muted"
          cursor="pointer"
          display="inline-flex"
          flexShrink={0}
          h="28px"
          justifyContent="center"
          transition="background 0.12s"
          w="28px"
        >
          <Icon icon="MoreHorizontal" size={4} />
        </MenuButton>
        <MenuList fontSize="ah-control" minW="160px" py={1}>
          <MenuItem
            fontWeight={600}
            px={3}
            py={2}
            onClick={() =>
              setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
              }, 50)
            }
          >
            Rename
          </MenuItem>
          <MenuItem
            color="red.600"
            fontWeight={600}
            px={3}
            py={2}
            onClick={() => removeCategory(category.id)}
          >
            Delete
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};
